import { Autocomplete, TextField } from '@mui/material';
import { useQuery } from '@parkchirp/api-client';
import { useMemo } from 'react';
import { LIST_TAX_TABLE_CITIES_BY_ORGANIZATION_ID_BY_CITIES } from '../../queries';

interface CityAutocompleteProps {
	organizationId: number;
	disabled: boolean;
	value: string | null;
	onChange: ({ label, value, timezone }: { label: string; value: string; timezone: string }) => void;
}

export const CityAutocomplete = ({ organizationId, value, disabled, onChange }: CityAutocompleteProps) => {
	const { loading: citiesLoading, data: cityData } = useQuery(LIST_TAX_TABLE_CITIES_BY_ORGANIZATION_ID_BY_CITIES, {
		variables: { organizationId: organizationId, filters: [], order: [{ field: 'name', sort: 'asc' }] },
		context: { apiVersion: 'v2' },
		skip: !organizationId,
		fetchPolicy: 'cache-and-network',
	});

	const cityOptions = useMemo(() => {
		return cityData?.listTaxTableCitiesByOrganizationIdByCities.map((orgCity) => ({
			label: `${orgCity.name} (${orgCity.state})`,
			value: orgCity.id,
		}));
	}, [cityData]);

	const cityTimezones = useMemo(() => {
		return (
			cityData?.listTaxTableCitiesByOrganizationIdByCities.reduce((acc, orgCity) => {
				acc[orgCity.id] = orgCity.timezone || 'UTC';
				return acc;
			}, {}) ?? {}
		);
	}, [cityData]);

	return (
		<Autocomplete
			size='small'
			id='city-autocomplete'
			options={cityOptions || []}
			getOptionLabel={(option) => option.label || ''}
			value={cityOptions?.find((opt) => opt.value === value) || null}
			onChange={(_, newValue) => {
				onChange(newValue ? { ...newValue, timezone: cityTimezones[newValue.value] } : '');
			}}
			fullWidth
			renderInput={(params) => (
				<TextField
					{...params}
					label='City'
					variant='outlined'
					fullWidth
					required
					inputProps={{
						...params.inputProps,
						'data-cy': 'promo-code-city-autocomplete',
					}}
				/>
			)}
			loading={citiesLoading}
			isOptionEqualToValue={(option, value) => option.value === value.value}
			renderOption={(props, option) => (
				<li {...props} key={option.value}>
					{option.label}
				</li>
			)}
			disabled={disabled}
		/>
	);
};
